import { useId, useRef, type ReactElement, type ReactNode } from "react";
import { IconButton } from "./Button.tsx";
import { classes } from "./classes.ts";
import { CentredRow } from "./Centred.tsx";
import { useCentred } from "./measure.ts";
import { KIT_METRICS, logical } from "./metrics.ts";
import { keepFocusBeside, useModal } from "./modal.ts";

/**
 * A dialog (brief section 5.1): the dialog frame over a modal scrim that fills the nearest positioned
 * ancestor, named by its title (`aria-labelledby`) and, when the page names the element that says what it
 * is about, described by it (`aria-describedby`). While it is open it is the topmost layer of the input
 * router, so Escape closes it through the router, and it holds the keyboard, so no key reaches the game
 * behind it; Tab and Shift+Tab stay inside it, focus that lands outside it is brought back, and a press on
 * its scrim beside it takes no focus from it. Escape only asks the page to close it (`onClose`): while the
 * page keeps it on the page, it keeps its layer (`keepLayer`), so the next Escape asks again rather than
 * reaching the game menu (`useModal`, which the kit's `ModalWindow` shares). It focuses its first safe control as it opens: the first control in its body or
 * actions that is not a danger button, else its close button, else its own box. It gives focus back, as it
 * closes, to what had focus as it opened, read while it first renders, before a control inside it that
 * focuses itself (`autoFocus`) takes focus. The dialog is placed in the middle of the scrim on whole logical
 * pixels.
 *
 * A dialog with actions centres its title, body and actions and has no close button, as the design draws it:
 * its own buttons (Cancel, OK) are the pointer and touch path Escape needs (brief rule 23). A dialog with no
 * actions, whose changes apply as they are made, keeps its title at the left beside a
 * close button, the only pointer and touch path to closing it (ADR-061's amendment "the kit and the entry forms
 * match the design").
 *
 * Dialogs stack: one opened while another is open, such as a confirm over the dialog that asked for it,
 * is a later sibling in the same positioned stage, never a child of the first's body, whose box is
 * positioned and would hold the second's scrim. Only the topmost dialog on the page keeps focus and Tab
 * inside it; one beneath does neither until it is on top again, and focus that comes back to it, such as
 * the control that opened the confirm, stays where it lands.
 */

/**
 * A modal dialog.
 *
 * @param props The `title`; the body; the `actions`, usually buttons, or none for a dialog whose changes
 *   apply as they are made, which then has no actions row under its body;
 *   `onClose`, which Escape and, on a dialog with no actions, the close button call to ask the page to take
 *   the dialog away, and which the page may refuse by keeping it mounted; its `width` in logical pixels; and `describedBy`, the id of an
 *   element in the body that describes the dialog, such as a confirm dialog's consequence, set as the
 *   dialog's `aria-describedby` only when given; and `hint`, a centred row under the actions, the dialog's
 *   `hintGap` under them, such as a confirm dialog's key hints; and `tone`, `danger` for a dialog that asks
 *   before an action that ends something, whose title the design draws in the danger colour.
 * @returns The scrim holding the dialog.
 * @throws Error when mounted outside a `KitInputProvider`.
 */
export function Dialog({ title, children, actions, hint, tone = "default", onClose, width, describedBy }: { readonly title: string; readonly children: ReactNode; readonly actions?: ReactNode; readonly hint?: ReactNode; readonly tone?: "default" | "danger"; readonly onClose: () => void; readonly width: number; readonly describedBy?: string }): ReactElement {
  const titleId = useId();
  const scrim = useRef<HTMLDivElement>(null);
  const dialog = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const header = useRef<HTMLDivElement>(null);
  const place = useCentred(scrim, dialog, { x: true, y: true }, null);
  const { trap } = useModal(dialog, () => ({ content: content.current === null ? [] : [content.current], close: header.current?.querySelector<HTMLElement>("button") ?? null }), onClose);

  const metrics = KIT_METRICS.dialog;
  const centred = actions !== undefined;
  return (
    <div ref={scrim} className="ot-scrim" data-kit="scrim" onMouseDown={keepFocusBeside}>
      <div
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={describedBy}
        tabIndex={-1}
        className={classes("ot-dialog", centred && "ot-dialog--centred", tone === "danger" && "ot-dialog--danger", "ot-ui-frame-dialog")}
        data-kit="dialog"
        onKeyDown={trap}
        style={{ width: logical(width), left: logical(place.left), top: logical(place.top), padding: logical(metrics.padding), gap: logical(metrics.gap) }}
      >
        <div ref={header} className="ot-dialog__header" data-kit="dialog-header" style={centred ? { minHeight: logical(metrics.title) } : { height: logical(metrics.header) }}>
          <h2 id={titleId} className="ot-dialog__title" style={{ marginTop: logical(centred ? 0 : metrics.titleOffset) }}>
            {title}
          </h2>
          {centred ? null : <IconButton icon="close" label="Close" onClick={onClose} />}
        </div>
        <div ref={content} className="ot-dialog__content" style={{ gap: logical(metrics.gap) }}>
          <div className="ot-dialog__body" data-kit="dialog-body">
            {children}
          </div>
          {actions === undefined ? null : (
            <CentredRow className="ot-dialog__actions" kit="dialog-actions" style={{ gap: logical(metrics.gap) }}>
              {actions}
            </CentredRow>
          )}
          {actions === undefined || hint === undefined ? null : (
            <CentredRow className="ot-dialog__hint" kit="dialog-hint" style={{ marginTop: logical(metrics.hintGap - metrics.gap) }}>
              {hint}
            </CentredRow>
          )}
        </div>
      </div>
    </div>
  );
}
